import { jsx as _jsx } from "react/jsx-runtime";
import { bytesToHex } from "@sundae/cardano-utils";
import { useI18N } from "@sundae/react-hooks";
import { Banner, Card, Loader, Tabs } from "@sundae/ui-toolkit";
import { useWalletApi, useWalletDetails } from "@sundae/wallet-react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import ErrorBox from "../../components/ErrorBox/ErrorBox.js";
import NoRewards from "../../components/NoRewards/index.js";
import PageContent from "../../components/PageContent/index.js";
import RewardsEstimate from "../../components/RewardsEstimate/index.js";
import WalletNotConnected from "../../components/WalletNotConnected/index.js";
import { useGetChosenUtxos } from "../../hooks/useGetChosenUtxos.js";
import { useIsWalletConnected } from "../../hooks/useIsWalletConnected.js";
import { useRewardAssetMetadata } from "../../hooks/useRewardAssetMetadata.js";
import { canClaimRewards, rewardClaimed, rewardExpired, useRewards, } from "../../hooks/useRewards.js";
import { useServerTime } from "../../hooks/useTime.js";
import { getAssetName, getFirstClaimableAsset, } from "../../utils/assets.utils.js";
import { unixToSlot } from "../../utils/slots.js";
const Rewards = () => {
    const { t } = useI18N("rewards");
    const { api } = useWalletApi();
    const { details: { mainAddress, network, stakingAddress }, } = useWalletDetails();
    const [selectedTab, setSelectedTab] = useState("0");
    const [cancelCooldown, setCancelCooldown] = useState(false);
    const isWalletConnected = useIsWalletConnected();
    const { data: [rewardAsset], } = useRewardAssetMetadata();
    const rewardsMutation = useRewards();
    const { mutation: { mutateAsync: getChosenUtxos, isLoading: isFetchingUtxos }, notEnoughADA, } = useGetChosenUtxos();
    const [showError, setShowError] = useState(null);
    const [utxos, setUtxos] = useState(null);
    const timeQuery = useServerTime();
    const time = timeQuery?.data?.now ?? new Date().toISOString();
    // Get the UTXOs once to seed the notEnoughADA property
    useEffect(() => {
        if (!api) {
            return;
        }
        ;
        (async () => {
            setUtxos(await getChosenUtxos(await api.getUtxos()));
        })();
    }, [api]);
    const claimableAddresses = useMemo(() => {
        const addressesWithUnclaimedRewards = {};
        for (const [key, value] of Object.entries(rewardsMutation?.data?.rewards ?? {})) {
            for (const reward of value) {
                if (canClaimRewards(reward, time, network)) {
                    addressesWithUnclaimedRewards[key] = true;
                }
            }
        }
        return Object.keys(addressesWithUnclaimedRewards);
    }, [rewardsMutation?.data]);
    const claimRewardMutation = useMutation([stakingAddress, mainAddress, claimableAddresses, utxos], async () => {
        if (!utxos) {
            // No utxos, so we can't claim
            return;
        }
        const body = {
            addresses: claimableAddresses.slice(0, 64),
            returnAddress: mainAddress,
            inputs: utxos.map(({ transaction_id: txId, index }) => ({
                txId,
                index: parseInt(index),
            })),
        };
        // simulate
        const data = await fetch(window.__APP_CONFIG.apiUrls.claim, {
            method: "POST",
            body: JSON.stringify(body),
        }).then((res) => res.json());
        // Declare these upfront, so we can use them within the catch if we have this data
        let signResponse;
        let txWithWitnesses;
        let txId;
        try {
            const cbor = await import("cbor-web");
            const rawTx = cbor.decode(data.txCborHex);
            const ttl = rawTx[0]?.get(3);
            const currentSlot = unixToSlot(Math.floor(new Date(time).valueOf() / 1000), network);
            if (ttl < currentSlot) {
                setShowError({
                    reportID: "",
                    type: "info",
                    message: t("cooldown"),
                });
                claimRewardMutation.reset();
                return;
            }
            signResponse = await api.signTx(data.txCborHex, true);
            const rawWitnesses = cbor.decode(signResponse);
            for (const witness of rawWitnesses.get(0)) {
                rawTx[1].get(0).push(witness);
            }
            txWithWitnesses = bytesToHex(cbor.encodeCanonical(rawTx));
            txId = await api.submitTx(txWithWitnesses);
            return txId;
        }
        catch (e) {
            let declinedToSign = /user rejected|user declined (to sign )?tx/i;
            let errorMessage = e?.message ?? e?.info ?? "";
            if (declinedToSign.test(errorMessage)) {
                setShowError({
                    reportID: crypto.randomUUID(),
                    type: "info",
                    message: t("declined"),
                });
                claimRewardMutation.reset();
                return null;
            }
            setShowError({
                reportID: crypto.randomUUID(),
                type: "error",
                message: t("error"),
                originalError: e,
                details: {
                    utxos,
                    data,
                    signResponse,
                    txWithWitnesses,
                },
            });
            return null;
        }
    }, {
        onError: (error) => {
            const regex = new RegExp(/no\savailable\sUTXOs$/);
            // If the error is an issue with available UTXOs, then we notify and keep trying for 1 minute.
            if (error instanceof Error && regex.test(error.message)) {
                setShowError({
                    reportID: crypto.randomUUID(),
                    type: "warning",
                    message: t("throttle"),
                });
            }
            else {
                setShowError({
                    reportID: crypto.randomUUID(),
                    type: "error",
                    message: t("error"),
                    originalError: error,
                });
                setTimeout(() => {
                    setShowError(null);
                }, 5000);
            }
        },
        retry: 0,
    });
    const getTabObj = (id, rewards, trigger, displayFooter) => {
        return {
            id,
            trigger,
            content: (_jsx(RewardsEstimate, { cancelCooldown: cancelCooldown, claimRewardMutation: claimRewardMutation, displayFooter: displayFooter, notEnoughADA: notEnoughADA, rewards: rewards, rewardsMutation: rewardsMutation, showMultiClaimWarning: claimableAddresses.length > 64, alert: showError && (_jsx(ErrorBox, { ...showError, reset: () => {
                        setShowError(null);
                        setCancelCooldown(true);
                        setTimeout(() => {
                            setCancelCooldown(false);
                        }, 5000);
                        claimRewardMutation.reset();
                    } })) })),
        };
    };
    const renderContent = () => {
        if (!isWalletConnected) {
            return (_jsx(WalletNotConnected, { copy: t("notFound.context.rewards.noWalletCopy", { ns: "not-found" }) }));
        }
        if (rewardsMutation?.isLoading || isFetchingUtxos) {
            return _jsx(Loader, { size: "medium" });
        }
        const rewards = [];
        for (const [, value] of Object.entries(rewardsMutation?.data?.rewards ?? {})) {
            rewards.push(...value);
        }
        if (rewardsMutation?.isSuccess && rewards.length > 0) {
            const claimedRewards = rewards.filter((r) => rewardClaimed(r) || rewardExpired(r, time));
            const unclaimedRewards = rewards.filter((r) => !rewardClaimed(r) && !rewardExpired(r, time));
            const sumUnclaimed = unclaimedRewards.reduce((s, p) => {
                s[p.assetId] = (s[p.assetId] ?? 0) + Number(p.amount);
                return s;
            }, {});
            const sumClaimed = claimedRewards.reduce((s, p) => {
                s[p.assetId] = (s[p.assetId] ?? 0) + Number(p.amount);
                return s;
            }, {});
            /**
             * @todo
             * Figure out a way to display multiple claimable assets when/if
             * we support that feature.
             */
            const assetId = getFirstClaimableAsset();
            return (_jsx(Card, { className: "reward-tabs m-auto w-full max-w-2xl", children: _jsx(Tabs, { value: selectedTab, onValueChange: setSelectedTab, data: [
                        getTabObj("0", unclaimedRewards, t("tabs.unclaimed", {
                            amount: (sumUnclaimed[assetId] ?? sumUnclaimed["undefined"] ?? 0) /
                                1e6,
                            asset: getAssetName(rewardAsset),
                        }), true),
                        getTabObj("1", claimedRewards, t("tabs.claimed", {
                            amount: (sumClaimed[assetId] ?? sumClaimed["undefined"] ?? 0) / 1e6,
                            asset: getAssetName(rewardAsset),
                        })),
                    ] }) }));
        }
        return _jsx(NoRewards, {});
    };
    return (_jsx(PageContent, { banner: _jsx(Banner, { i18n: {
                ariaLabel: t("banner.ariaLabel"),
                copy: t("banner.copy"),
                title: t("banner.title"),
            }, image: `` }), className: classNames("flex flex-col items-center gap-10"), children: renderContent() }));
};
export default Rewards;
//# sourceMappingURL=Rewards.js.map